import { ExternalLink, Link, Package, Percent, Tags } from 'lucide-react';
import { useId } from 'react';
import {
  useTranslation,
  type AdminStorePromotionalAdDestination,
  type PromotionalAdDestinationType,
  type TranslationKey,
} from '@dukanti/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { PromotionalAdCategoryPicker } from './promotional-ad-category-picker';
import { PromotionalAdProductPicker } from './promotional-ad-product-picker';
import type { PromotionalAdFormValues } from '../lib/promotional-ad-form-schema';

export interface PromotionalAdDestinationSectionProps {
  readonly storeId: string | null;
  readonly value: PromotionalAdFormValues['destination'];
  readonly initialDestination?: AdminStorePromotionalAdDestination | null;
  readonly disabled?: boolean;
  readonly errorKey?: TranslationKey | null;
  readonly onChange: (destination: PromotionalAdFormValues['destination']) => void;
}

interface DestinationTypeOption {
  readonly type: PromotionalAdDestinationType;
  readonly labelKey: TranslationKey;
  readonly descriptionKey: TranslationKey;
  readonly icon: typeof Link;
}

const DESTINATION_TYPE_OPTIONS: readonly DestinationTypeOption[] = [
  {
    type: 'none',
    labelKey: 'admin.promotionalAds.destination.none',
    descriptionKey: 'admin.promotionalAds.destination.noneDescription',
    icon: Link,
  },
  {
    type: 'sales',
    labelKey: 'admin.promotionalAds.destination.sales',
    descriptionKey: 'admin.promotionalAds.destination.salesDescription',
    icon: Percent,
  },
  {
    type: 'category',
    labelKey: 'admin.promotionalAds.destination.category',
    descriptionKey: 'admin.promotionalAds.destination.categoryDescription',
    icon: Tags,
  },
  {
    type: 'product',
    labelKey: 'admin.promotionalAds.destination.product',
    descriptionKey: 'admin.promotionalAds.destination.productDescription',
    icon: Package,
  },
  {
    type: 'external',
    labelKey: 'admin.promotionalAds.destination.external',
    descriptionKey: 'admin.promotionalAds.destination.externalDescription',
    icon: ExternalLink,
  },
];

function destinationForType(
  type: PromotionalAdDestinationType,
): PromotionalAdFormValues['destination'] {
  switch (type) {
    case 'sales':
      return { type: 'sales' };
    case 'category':
      return { type: 'category', categoryId: '' };
    case 'product':
      return { type: 'product', productId: '' };
    case 'external':
      return { type: 'external', url: '' };
    default:
      return { type: 'none' };
  }
}

export function PromotionalAdDestinationSection({
  storeId,
  value,
  initialDestination,
  disabled = false,
  errorKey,
  onChange,
}: PromotionalAdDestinationSectionProps) {
  const { t, dir } = useTranslation();
  const sectionTitleId = useId();
  const selectedType = value.type;

  return (
    <section
      dir={dir}
      className="border-border bg-admin-surface-muted rounded-lg border p-4"
      aria-labelledby={sectionTitleId}
      data-testid="promotional-ad-destination-section"
    >
      <div className="mb-4">
        <h2 id={sectionTitleId} className="text-foreground text-start text-sm font-semibold">
          {t('admin.promotionalAds.destination.title' as TranslationKey)}
        </h2>
        <p className="text-muted-foreground mt-1 text-start text-sm">
          {t('admin.promotionalAds.destination.description' as TranslationKey)}
        </p>
      </div>

      <fieldset className="space-y-3">
        <legend className="sr-only">
          {t('admin.promotionalAds.destination.typeLabel' as TranslationKey)}
        </legend>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {DESTINATION_TYPE_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedType === option.type;
            return (
              <button
                key={option.type}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={disabled}
                onClick={() => onChange(destinationForType(option.type))}
                className={cn(
                  'border-border bg-card hover:bg-admin-surface-muted flex min-h-24 items-start gap-3 rounded-lg border p-3 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                  isSelected && 'border-admin-lime ring-admin-lime/30 ring-2',
                )}
                data-testid={`promotional-ad-destination-type-${option.type}`}
              >
                <Icon className="text-zaatar mt-0.5 size-4 shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="text-foreground block text-sm font-medium">
                    {t(option.labelKey)}
                  </span>
                  <span className="text-muted-foreground mt-1 block text-xs leading-5">
                    {t(option.descriptionKey)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-4">
        {value.type === 'category' ? (
          <PromotionalAdCategoryPicker
            storeId={storeId}
            selectedCategoryId={value.categoryId || null}
            initialDestination={initialDestination}
            disabled={disabled}
            errorKey={errorKey}
            onSelect={(categoryId) => onChange({ type: 'category', categoryId })}
            onClear={() => onChange({ type: 'category', categoryId: '' })}
          />
        ) : null}

        {value.type === 'product' ? (
          <PromotionalAdProductPicker
            storeId={storeId}
            selectedProductId={value.productId || null}
            initialDestination={initialDestination}
            disabled={disabled}
            errorKey={errorKey}
            onSelect={(productId) => onChange({ type: 'product', productId })}
            onClear={() => onChange({ type: 'product', productId: '' })}
          />
        ) : null}

        {value.type === 'external' ? (
          <div>
            <Label htmlFor="promotional-ad-external-url" className="mb-1.5 block text-start">
              {t('admin.promotionalAds.destination.externalLabel' as TranslationKey)}
            </Label>
            <Input
              id="promotional-ad-external-url"
              type="url"
              dir="ltr"
              value={value.url}
              disabled={disabled}
              autoComplete="off"
              placeholder={t(
                'admin.promotionalAds.destination.externalPlaceholder' as TranslationKey,
              )}
              aria-invalid={!!errorKey}
              aria-describedby={errorKey ? 'promotional-ad-external-url-error' : undefined}
              data-testid="promotional-ad-external-url-input"
              onChange={(event) => onChange({ type: 'external', url: event.currentTarget.value })}
            />
            {errorKey ? (
              <p
                id="promotional-ad-external-url-error"
                role="alert"
                className="text-destructive mt-1 text-start text-xs"
              >
                {t(errorKey)}
              </p>
            ) : null}
          </div>
        ) : null}

        {value.type !== 'category' && value.type !== 'product' && value.type !== 'external' ? (
          errorKey ? (
            <p role="alert" className="text-destructive text-start text-xs">
              {t(errorKey)}
            </p>
          ) : null
        ) : null}
      </div>
    </section>
  );
}
